import { ChevronDown, ChevronRight, Info, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { StatusBadge } from "../../components/ui/statusBadge";
import { Textarea } from "../../components/ui/textarea";
import { formatDatetime, usernameFromEmail, usernamesOnly } from "../../lib/format";
import { protocolOf } from "../../lib/url";
import { useReadOnly } from "../lock/ReadOnlyContext";
import type { StipulationView } from "./api/types";
import { useStipulationHistory } from "./api/useStipulationHistory";
import StipulationActionMenu from "./StipulationActionMenu";
import { useStipulationRowActions } from "./useStipulationRowActions";

interface StipulationRowProperties {
  approvalId?: string;
  stip: StipulationView;
}

// The BFF-sourced `policyTag` is rendered as a link `href`; only allow http(s)
// so a malformed/malicious value (e.g. `javascript:`) can't execute on click.
function isSafePolicyUrl(url: string): boolean {
  const protocol = protocolOf(url);
  return protocol !== undefined && ["http:", "https:"].includes(protocol);
}

// One stipulation card: expand toggle, title, policy link, status badge, action
// menu, optional dealer-instruction note, activity line, and an expandable
// Instructions History. Selections + the note are staged in the edits store so
// the UI updates immediately, then persisted per-row through the BFF action API.
function StipulationRow({ approvalId, stip }: StipulationRowProperties) {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const [expanded, setExpanded] = useState(false);
  const {
    selectedAction,
    selectedReason,
    currentLabel,
    note,
    porExceptionChecked,
    isSaving,
    isPorSaving,
    handleSelect,
    persistAction,
    setNote,
    handlePorExceptionChange,
  } = useStipulationRowActions(approvalId, stip);

  const { data: fetchedHistory, isLoading: isHistoryLoading } = useStipulationHistory({
    approvalId: approvalId ?? "",
    stipCode: stip.stipCode,
    applicantPk: stip.applicantPk,
    enabled: expanded,
  });

  // Show pre-loaded list data immediately; replace with fresh fetch when available.
  const actionHistory = fetchedHistory ?? stip.history;

  return (
    <div className="border-border rounded border border-l-2 p-1.5">
      <div className="flex items-start gap-1.5">
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={t("stipulations.toggleDetails")}
          onClick={() => setExpanded((previous) => !previous)}
          className="text-muted-foreground hover:text-foreground shrink-0 p-0.5"
        >
          {expanded ? (
            <ChevronDown aria-hidden="true" className="size-3.5" />
          ) : (
            <ChevronRight aria-hidden="true" className="size-3.5" />
          )}
        </button>
        <span className="text-foreground min-w-0 flex-1 text-xs font-semibold">{stip.title}</span>
      </div>

      {stip.availableActions.length > 0 ||
      stip.openLoanStip ||
      stip.secondaryProofsAllowed ||
      stip.policyTag ||
      stip.currentAction ? (
        <div className="mt-1 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            {stip.availableActions.length > 0 && !readOnly && (
              <StipulationActionMenu
                actions={stip.availableActions}
                currentLabel={currentLabel}
                disabled={isSaving}
                onSelect={handleSelect}
              />
            )}
            {stip.openLoanStip && (
              <button
                type="button"
                onClick={() => void 0}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded px-1.5 py-0.5 text-xs"
              >
                {t("stipulations.openLoanCheck")}
              </button>
            )}
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {stip.policyTag && isSafePolicyUrl(stip.policyTag) && (
                <a
                  href={stip.policyTag}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("stipulations.policyAriaLabel")}
                  className="text-foreground hover:bg-accent rounded-full p-1"
                >
                  <Info aria-hidden="true" className="size-4" />
                </a>
              )}
              {stip.currentAction && (
                <StatusBadge
                  status="open"
                  label={stip.currentAction}
                  className={
                    stip.statusDisplay === "open"
                      ? "border-success-strong text-success-strong"
                      : "border-border text-muted-foreground"
                  }
                />
              )}
            </div>
          </div>
          {stip.secondaryProofsAllowed && (
            <label className="flex items-center gap-1.5 text-xs">
              <span>{t("stipulations.secondaryProofsLabel")}</span>
              <input
                type="checkbox"
                checked={porExceptionChecked}
                onChange={(event) => handlePorExceptionChange(event.target.checked)}
                disabled={isPorSaving || readOnly}
                className="size-4"
              />
            </label>
          )}
        </div>
      ) : null}

      {selectedAction?.requiresNote && (
        <div className="mt-1">
          <Textarea
            rows={2}
            value={note ?? ""}
            onChange={(event) => setNote(event.target.value)}
            onBlur={(event) => {
              persistAction(selectedAction.actionCode, selectedReason?.reasonPk, event.target.value);
            }}
            placeholder={t("stipulations.dealerInstructionPlaceholder")}
            className="text-xs"
          />
        </div>
      )}

      {stip.recentNote && (stip.recentNote.actionName || stip.recentNote.auditInformation) && (
        <div className="text-muted-foreground mt-1 text-xs leading-tight">
          <div className="flex gap-1">
            {stip.recentNote.actionName && (
              <span className="text-primary font-semibold">{stip.recentNote.actionName}</span>
            )}
            {stip.recentNote.auditInformation && (
              <span className="text-2xs">{usernamesOnly(stip.recentNote.auditInformation)}</span>
            )}
          </div>
          {stip.recentNote.actionName && stip.recentNote.description && <div>{stip.recentNote.description}</div>}
        </div>
      )}

      {expanded && (
        <fieldset className="border-border mt-1.5 rounded border p-1.5">
          <legend className="text-foreground px-1 text-xs font-semibold">
            {t("stipulations.instructionsHistory")}
          </legend>
          {isHistoryLoading && actionHistory.length === 0 ? (
            <div className="text-muted-foreground flex items-center gap-1 text-xs">
              <Loader2 aria-hidden="true" className="size-3 animate-spin" />
              {t("stipulations.historyLoading")}
            </div>
          ) : (
            actionHistory &&
            actionHistory.length > 0 && (
              <ul className="text-muted-foreground mb-1 flex flex-col gap-0.5 text-xs">
                {actionHistory.map((entry, index) => (
                  <li key={index} className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1">
                      {entry.actionName && <span className="text-primary font-bold">{entry.actionName}</span>}
                      <span className="text-2xs">
                        {[
                          entry.performedAt ? formatDatetime(entry.performedAt) : undefined,
                          entry.performedBy ? usernameFromEmail(entry.performedBy) : undefined,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                    {(entry.reasonDescription ?? entry.noteText) && (
                      <div>{entry.reasonDescription ?? entry.noteText}</div>
                    )}
                  </li>
                ))}
              </ul>
            )
          )}
        </fieldset>
      )}
    </div>
  );
}

export default StipulationRow;
